import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  approveLibraryComposition,
  fetchLibrary,
  fetchLibraryDetail,
  fetchLibraryIncompatible,
  libraryPictureUrl,
  removeLibraryPicture,
  setLibraryEnabled,
  uploadLibraryPicture,
  type LibraryDetail,
  type LibraryEntry,
} from '../api/catalog';
import { ApiError } from '../api/http';
import { ca } from '../i18n/ca';
import { AnchorSketch, type SketchAnchor } from './AnchorSketch';
import { CompositionFootprint } from './CompositionFootprint';
import { eraLabel, theatresOf, useEras } from './useEras';

const t = ca.catalogPage2.library;

/** The anchors an imported composition carries (`anchors` of its body), as the sketch draws them. */
function sketchAnchors(raw: Record<string, unknown> | null | undefined): SketchAnchor[] {
  if (!raw) return [];
  const one = (name: string, index: number, v: unknown): SketchAnchor | null => {
    const a = v as { offset?: number[]; azimuth?: number } | null;
    if (!a || !Array.isArray(a.offset) || a.offset.length < 2) return null;
    return { name, index, x: a.offset[0], y: a.offset[1], azimuth: a.azimuth ?? 90, placed: true };
  };
  const out: SketchAnchor[] = [];
  Object.entries(raw).forEach(([name, v]) => {
    if (Array.isArray(v))
      v.forEach((s, i) => {
        const a = one(name, i, s);
        if (a) out.push(a);
      });
    else {
      const a = one(name, 0, v);
      if (a) out.push(a);
    }
  });
  return out;
}

type RoleFilter = '' | 'start_base' | 'fob' | 'scene';
type StateFilter = 'all' | 'enabled' | 'unapproved' | 'disabled';

/** A role as a person reads it: a recipe's scene by its recipe. */
function roleName(role: string): string {
  if (role.startsWith('scene:')) return t.sceneOf(role.split(':')[1] ?? '');
  return t.roleNames[role] ?? role;
}

/** The state a card shows: on, waiting for the owner's approval, or off. */
function stateOf(e: LibraryEntry): { label: string; tone: string } {
  if (e.enabled) return { label: t.chipEnabled, tone: 'ok' };
  if (!e.approved) return { label: t.chipUnapproved, tone: 'warn' };
  return { label: t.chipDisabled, tone: '' };
}

/**
 * The composition library of one mod set (docs/design/COMPOSITION_LIBRARY.md
 * §6): every start base, forward post and recipe scene the mod set can build
 * whole, filtered by era, theatre, role and state. An admin turns one on or
 * off and approves a version; on is refused until the version is approved
 * (the owner, 2026-10-25). Pictures come with the bench's batches; until a
 * composition has them, its page draws its footprint.
 */
export function CompositionLibraryView({
  modSetHash,
  isAdmin,
}: {
  modSetHash: string | null;
  isAdmin: boolean;
}) {
  const eras = useEras();
  const [entries, setEntries] = useState<LibraryEntry[]>([]);
  const [scanned, setScanned] = useState(true);
  const [era, setEra] = useState('');
  const [theatre, setTheatre] = useState('');
  const [role, setRole] = useState<RoleFilter>('');
  const [state, setState] = useState<StateFilter>('all');
  const [open, setOpen] = useState<LibraryDetail | null>(null);
  const [reason, setReason] = useState('');
  const [incompatible, setIncompatible] = useState<LibraryEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [uploadView, setUploadView] = useState('TOP');
  const [uploadWorld, setUploadWorld] = useState('');

  const reload = useCallback(() => {
    if (!modSetHash) return;
    fetchLibrary(modSetHash)
      .then((v) => {
        setScanned(v.scanned);
        setEntries(v.compositions);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [modSetHash]);

  useEffect(reload, [reload]);
  useEffect(() => {
    if (!isAdmin) return;
    fetchLibraryIncompatible()
      .then(setIncompatible)
      .catch(() => setIncompatible([]));
  }, [isAdmin]);

  const theatres = era ? theatresOf(eras, era) : [];
  const shown = useMemo(
    () =>
      entries.filter((e) => {
        if (era && !e.eras.includes(era)) return false;
        if (
          theatre &&
          e.tags.length > 0 &&
          !e.tags.some((g) => g.era === era && (!g.theatre || g.theatre === theatre))
        )
          return false;
        if (role && !e.roles.some((r) => r === role || r.startsWith(`${role}:`))) return false;
        if (state === 'enabled' && !e.enabled) return false;
        if (state === 'unapproved' && e.approved) return false;
        if (state === 'disabled' && (e.enabled || !e.approved)) return false;
        return true;
      }),
    [entries, era, theatre, role, state],
  );

  /** One entry changed: in the list and on the open page. */
  const replace = (next: LibraryEntry) => {
    setEntries((list) => list.map((e) => (e.id === next.id ? next : e)));
    setOpen((o) => (o && o.entry.id === next.id ? { ...o, entry: next } : o));
  };

  async function toggle(e: LibraryEntry, why: string | null) {
    setError(null);
    try {
      replace(await setLibraryEnabled(e.id, !e.enabled, e.enabled ? why : null, modSetHash));
      setReason('');
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'not_approved' ? t.notApproved : String(err),
      );
    }
  }

  async function approve(e: LibraryEntry) {
    setError(null);
    try {
      replace(await approveLibraryComposition(e.id, modSetHash));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  /** A file library_pictures.py named `<key>__<VIEW>__<world>.jpg` says its own view and terrain. */
  const named = (file: File) => {
    const m = /__([A-Z_]+)__([A-Za-z0-9_-]+)\.(jpe?g|png|webp)$/i.exec(file.name);
    return m && t.views[m[1].toUpperCase()] ? { view: m[1].toUpperCase(), world: m[2] } : null;
  };

  async function uploadPictures(e: LibraryEntry, files: File[]) {
    setError(null);
    setNotice(null);
    let done = 0;
    for (const file of files) {
      const said = named(file) ?? { view: uploadView, world: uploadWorld.trim() };
      try {
        await uploadLibraryPicture(e.id, file, said.view, said.world);
        done += 1;
      } catch (err) {
        const code = err instanceof Error ? err.message : String(err);
        setError(`${file.name}: ${t.pictureErrors[code] ?? code}`);
        break;
      }
    }
    if (done > 0) {
      setNotice(t.uploaded(done));
      await show(e);
      reload();
    }
  }

  async function removePicture(e: LibraryEntry, pictureId: number) {
    setError(null);
    try {
      await removeLibraryPicture(e.id, pictureId);
      await show(e);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function show(e: LibraryEntry) {
    try {
      setOpen(await fetchLibraryDetail(e.id, modSetHash));
      setReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  if (!modSetHash || !scanned) return <p className="muted small">{t.notScanned}</p>;

  const switchFor = (e: LibraryEntry, why: string | null) =>
    isAdmin && (
      <label
        className="switch"
        title={!e.enabled && !e.approved ? t.switchNeedsApproval : undefined}
      >
        <input
          type="checkbox"
          role="switch"
          name={`enabled-${e.id}`}
          checked={e.enabled}
          disabled={!e.enabled && !e.approved}
          aria-label={e.enabled ? t.switchOff(e.name) : t.switchOn(e.name)}
          onChange={() => void toggle(e, why)}
        />
        {e.enabled ? t.chipEnabled : t.chipDisabled}
      </label>
    );

  return (
    <div className="composition-library">
      <p className="muted small">{t.intro}</p>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="muted small" role="status">
          {notice}
        </p>
      )}

      <div className="card library-filters" role="group" aria-label={t.era}>
        <div className="row wrap">
          <span className="library-label">{t.era}</span>
          <button
            type="button"
            className={`chip toggle ${era === '' ? 'on' : ''}`}
            aria-pressed={era === ''}
            onClick={() => {
              setEra('');
              setTheatre('');
            }}
          >
            {t.all}
          </button>
          {eras.map((e) => (
            <button
              key={e.id}
              type="button"
              className={`chip toggle ${era === e.id ? 'on' : ''}`}
              aria-pressed={era === e.id}
              onClick={() => {
                setEra(e.id);
                setTheatre('');
              }}
            >
              {e.label}
            </button>
          ))}
        </div>
        {theatres.length > 0 && (
          <div className="row wrap">
            <span className="library-label">{t.theatre}</span>
            <button
              type="button"
              className={`chip toggle ${theatre === '' ? 'on' : ''}`}
              aria-pressed={theatre === ''}
              onClick={() => setTheatre('')}
            >
              {t.allTheatres}
            </button>
            {theatres.map((th) => (
              <button
                key={th.id}
                type="button"
                className={`chip toggle ${theatre === th.id ? 'on' : ''}`}
                aria-pressed={theatre === th.id}
                title={th.label}
                onClick={() => setTheatre(th.id)}
              >
                {th.chip}
              </button>
            ))}
          </div>
        )}
        <div className="row wrap">
          <span className="library-label">{t.role}</span>
          {(['', 'start_base', 'fob', 'scene'] as RoleFilter[]).map((r) => (
            <button
              key={r || 'all'}
              type="button"
              className={`chip toggle ${role === r ? 'on' : ''}`}
              aria-pressed={role === r}
              onClick={() => setRole(r)}
            >
              {r ? t.roles[r] : t.allTheatres}
            </button>
          ))}
          <label className="library-state">
            {t.state}
            <select
              name="state"
              value={state}
              onChange={(e) => setState(e.target.value as StateFilter)}
            >
              {(['all', 'enabled', 'unapproved', 'disabled'] as StateFilter[]).map((s) => (
                <option key={s} value={s}>
                  {t.states[s]}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="row between">
        <h3>{t.count(shown.length)}</h3>
        {isAdmin && incompatible.length > 0 && (
          <details className="library-incompatible">
            <summary>
              <i className="bi bi-exclamation-triangle" aria-hidden="true" />{' '}
              {t.incompatible(incompatible.length)}
            </summary>
            <p className="muted small">{t.incompatibleHint}</p>
            <ul className="list">
              {incompatible.map((e) => (
                <li key={e.id}>
                  <strong>{e.name}</strong>{' '}
                  <span className="muted small">{t.missingCount(e.missing.length)}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>

      {shown.length === 0 && <p className="muted small">{t.empty}</p>}
      <ul className="library-grid">
        {shown.map((e) => {
          const st = stateOf(e);
          return (
            <li key={e.id} className="card library-card">
              <button
                type="button"
                className="library-picture"
                aria-label={t.open(e.name)}
                onClick={() => void show(e)}
              >
                {e.pictures.length > 0 ? (
                  <img
                    src={libraryPictureUrl(e.id, e.pictures[0].id)}
                    alt={t.pictureOf(
                      t.views[e.pictures[0].view] ?? e.pictures[0].view,
                      e.pictures[0].world,
                    )}
                    loading="lazy"
                  />
                ) : (
                  <>
                    <i className="bi bi-bounding-box" aria-hidden="true" />
                    <span className="muted small">{t.noPictures}</span>
                  </>
                )}
              </button>
              <div className="library-body">
                <div className="row between">
                  <span>
                    <strong>{e.name}</strong>
                    <span className="muted small library-origin">
                      {t.origins[e.origin] ?? e.origin}
                      {e.author ? ` · ${e.author}` : ''}
                    </span>
                  </span>
                  {switchFor(e, null)}
                </div>
                <div className="row wrap">
                  <span className={`chip ${st.tone}`}>{st.label}</span>
                  {e.enabled && !e.approved && (
                    <span className="chip warn">{t.chipUnapproved}</span>
                  )}
                  <span className="chip">{t.footprint(e.footprint)}</span>
                  {e.roles.map((r) => (
                    <span key={r} className="chip">
                      {roleName(r)}
                    </span>
                  ))}
                  {e.tags.map((g) => (
                    <span key={`${g.era}-${g.theatre ?? ''}`} className="chip">
                      {eraLabel(eras, g.era)}
                      {g.theatre
                        ? ` · ${theatresOf(eras, g.era).find((th) => th.id === g.theatre)?.chip ?? g.theatre}`
                        : ''}
                    </span>
                  ))}
                </div>
                {e.disabledReason && (
                  <p className="muted small">{t.disabledBecause(e.disabledReason)}</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {open && (
        <section className="card library-detail" aria-label={open.entry.name}>
          <div className="row between">
            <h3>{open.entry.name}</h3>
            <button type="button" onClick={() => setOpen(null)}>
              {t.close}
            </button>
          </div>
          {open.entry.pictures.length > 0 && (
            <div className="library-gallery" aria-label={t.pictures}>
              {open.entry.pictures.map((p) => (
                <figure key={p.id}>
                  <img
                    src={libraryPictureUrl(open.entry.id, p.id)}
                    alt={t.pictureOf(t.views[p.view] ?? p.view, p.world)}
                  />
                  <figcaption className="muted small">
                    {t.pictureOf(t.views[p.view] ?? p.view, p.world)}
                    {isAdmin && (
                      <button
                        type="button"
                        className="small icon-only"
                        aria-label={t.removePicture(t.views[p.view] ?? p.view)}
                        onClick={() => void removePicture(open.entry, p.id)}
                      >
                        <i className="bi bi-trash" aria-hidden="true" />
                      </button>
                    )}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
          <div className="row wrap library-detail-body">
            {sketchAnchors(open.anchors).length > 0 ? (
              <AnchorSketch
                objects={open.objects}
                anchors={sketchAnchors(open.anchors)}
                footprint={open.entry.footprint}
                size={260}
              />
            ) : (
              <CompositionFootprint body={{ objects: open.objects }} size={200} />
            )}
            <dl className="facts">
              <dt>{t.origin}</dt>
              <dd>{t.origins[open.entry.origin] ?? open.entry.origin}</dd>
              {open.entry.author && (
                <>
                  <dt>{t.author}</dt>
                  <dd>{open.entry.author}</dd>
                </>
              )}
              {open.entry.licence && (
                <>
                  <dt>{t.licence}</dt>
                  <dd>{open.entry.licence}</dd>
                </>
              )}
              {open.workshop?.link && (
                <>
                  <dt>{t.workshop.itemTitle}</dt>
                  <dd>
                    <a href={open.workshop.link} target="_blank" rel="noreferrer noopener">
                      {open.workshop.title ?? open.workshop.id}
                    </a>
                    {open.workshop.updated && (
                      <span className="muted small">
                        {' '}
                        {t.workshop.itemUpdated(open.workshop.updated.slice(0, 10))}
                      </span>
                    )}
                  </dd>
                </>
              )}
              {open.imported && (
                <>
                  <dt>{t.workshop.leftOut}</dt>
                  <dd>
                    {Object.entries(open.imported.dropped)
                      .filter(([k, n]) => n > 0 && t.workshop.dropped[k])
                      .map(([k, n]) => t.workshop.dropped[k](n))
                      .join(' · ') || '—'}
                  </dd>
                </>
              )}
              <dt>{t.erasHere}</dt>
              <dd>{open.entry.eras.map((e) => eraLabel(eras, e)).join(', ')}</dd>
              <dt>{t.details}</dt>
              <dd>
                {t.footprint(open.entry.footprint)} · {t.classes(open.classes.length)}
              </dd>
            </dl>
          </div>
          {isAdmin && (
            <div className="row wrap library-admin">
              {open.entry.enabled && (
                <label>
                  {t.reason}
                  <input
                    type="text"
                    name="reason"
                    value={reason}
                    maxLength={255}
                    placeholder={t.reasonPlaceholder}
                    onChange={(ev) => setReason(ev.target.value)}
                  />
                </label>
              )}
              {switchFor(open.entry, reason || null)}
              <button
                type="button"
                className={open.entry.approved ? '' : 'primary'}
                disabled={open.entry.approved}
                onClick={() => void approve(open.entry)}
              >
                <i className="bi bi-check2-circle" aria-hidden="true" />{' '}
                {open.entry.approved ? t.approvedAlready : t.approve}
              </button>
            </div>
          )}
          {isAdmin && (
            <div className="row wrap library-upload">
              <label>
                {t.uploadView}
                <select
                  name="view"
                  value={uploadView}
                  onChange={(ev) => setUploadView(ev.target.value)}
                >
                  {Object.keys(t.views).map((v) => (
                    <option key={v} value={v}>
                      {t.views[v]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t.uploadWorld}
                <input
                  type="text"
                  name="world"
                  value={uploadWorld}
                  placeholder="iron_excelsior_Tobruk"
                  onChange={(ev) => setUploadWorld(ev.target.value)}
                />
              </label>
              <label className="library-upload-file">
                <span>
                  <i className="bi bi-images" aria-hidden="true" /> {t.upload}
                </span>
                <input
                  type="file"
                  name="pictures"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={(ev) => {
                    const files = Array.from(ev.target.files ?? []);
                    ev.target.value = '';
                    void uploadPictures(open.entry, files);
                  }}
                />
              </label>
              <p className="muted small">{t.uploadHint}</p>
            </div>
          )}
          {open.entry.approved && open.entry.approvedAt && (
            <p className="muted small">{t.approved(open.entry.approvedAt.slice(0, 10))}</p>
          )}
        </section>
      )}
    </div>
  );
}
